import type {
  BulkShiftGapRowType,
  SelectedGapEntryType,
} from './_bulk-shift-step-types';

export function toGapEntry(gap: BulkShiftGapRowType): SelectedGapEntryType {
  return {
    shiftGapId: gap.id,
    startDatetime: gap.startDatetime,
    shiftDuration: gap.shiftDuration,
    timeZone: gap.timeZone,
    dayOfWeek: gap.dayOfWeek,
    timeOfDay: gap.timeOfDay ?? null,
    organizationUnit: gap.organizationUnit,
    role: gap.role ?? null,
  };
}

export function addGap(
  current: readonly SelectedGapEntryType[],
  entry: SelectedGapEntryType
): SelectedGapEntryType[] {
  if (current.some((existing) => existing.shiftGapId === entry.shiftGapId)) {
    return [...current];
  }
  return [...current, entry];
}

export function removeGap(
  current: readonly SelectedGapEntryType[],
  shiftGapId: string
): SelectedGapEntryType[] {
  return current.filter((entry) => entry.shiftGapId !== shiftGapId);
}

type RangeSelectionInput = {
  current: readonly SelectedGapEntryType[];
  pageGaps: readonly BulkShiftGapRowType[];
  fromGapId: string;
  toGapId: string;
  selected: boolean;
};

// Toggle every gap between fromGapId and toGapId (inclusive) on the current
// page to `selected`. Used by Shift+click range selection across rows.
export function applyRangeSelection({
  current,
  pageGaps,
  fromGapId,
  toGapId,
  selected,
}: RangeSelectionInput): SelectedGapEntryType[] {
  const fromIndex = pageGaps.findIndex((gap) => gap.id === fromGapId);
  const toIndex = pageGaps.findIndex((gap) => gap.id === toGapId);

  if (fromIndex === -1 || toIndex === -1) {
    return [...current];
  }

  const [start, end] =
    fromIndex <= toIndex ? [fromIndex, toIndex] : [toIndex, fromIndex];
  const slice = pageGaps.slice(start, end + 1);

  if (selected) {
    return slice.reduce<SelectedGapEntryType[]>(
      (acc, gap) => addGap(acc, toGapEntry(gap)),
      [...current]
    );
  }

  const removeIds = new Set(slice.map((gap) => gap.id));
  return current.filter((entry) => !removeIds.has(entry.shiftGapId));
}

type TogglePageInput = {
  current: readonly SelectedGapEntryType[];
  pageGaps: readonly BulkShiftGapRowType[];
  selected: boolean;
};

// Header-checkbox behavior: select or deselect every gap on the current page,
// leaving gaps from other pages alone.
export function applyTogglePage({
  current,
  pageGaps,
  selected,
}: TogglePageInput): SelectedGapEntryType[] {
  if (selected) {
    return pageGaps.reduce<SelectedGapEntryType[]>(
      (acc, gap) => addGap(acc, toGapEntry(gap)),
      [...current]
    );
  }

  const pageGapIds = new Set(pageGaps.map((gap) => gap.id));
  return current.filter((entry) => !pageGapIds.has(entry.shiftGapId));
}
